/** Stage E3.2 — AI Coaching panel + page */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, X, Clock, CheckCircle2, ThumbsDown, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TIER_STYLES = {
  emerging: "text-muted-foreground border-current",
  moderate: "text-signal-yellow border-current",
  strong: "pnl-up border-current",
};

const FEEDBACK_ACTIONS = [
  { code: "helpful", label: "Helpful", Icon: CheckCircle2 },
  { code: "not_helpful", label: "Not helpful", Icon: ThumbsDown },
  { code: "incorrect", label: "Incorrect", Icon: X },
  { code: "already_known", label: "Already known", Icon: HelpCircle },
  { code: "review_later", label: "Review later", Icon: Clock },
];

function InsightCard({ insight, onFeedback }) {
  const [busy, setBusy] = useState(false);
  const tone = (insight.effect_size || 0) >= 0 ? "pnl-up" : "pnl-down";
  const Icon = (insight.effect_size || 0) >= 0 ? TrendingUp : TrendingDown;
  const act = async (code) => {
    setBusy(true);
    try { await onFeedback(insight.id, code); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border/60 p-3 space-y-2" data-testid={`coaching-insight-${insight.category}`}>
      <div className="flex items-baseline gap-2">
        <Icon size={13} className={tone}/>
        <span className="text-sm font-medium">{insight.title}</span>
        <span className={`chip !text-[10px] uppercase ${TIER_STYLES[insight.confidence_tier] || ""}`}>{insight.confidence_tier}</span>
        <span className="ml-auto mono text-[10px] text-muted-foreground">n={insight.sample_size} · effect {(insight.effect_size * 100).toFixed(1)}pp</span>
      </div>
      <p className="text-xs text-foreground/85 leading-relaxed">{insight.observation}</p>
      {insight.evidence && (
        <div className="border border-border/30 bg-background/40 p-2">
          <div className="label-cap mb-1">Evidence</div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] mono">
            {Object.entries(insight.evidence).map(([k, v]) => (
              <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {String(v)}</div>
            ))}
          </div>
        </div>
      )}
      {insight.suggested_review_action && (
        <div className="text-[11px] italic text-muted-foreground">Suggested action: {insight.suggested_review_action}</div>
      )}
      {insight.user_feedback ? (
        <div className="mono text-[10px] text-muted-foreground">Feedback recorded: {insight.user_feedback.replace(/_/g, " ")}</div>
      ) : (
        <div className="flex items-center flex-wrap gap-1 pt-1">
          {FEEDBACK_ACTIONS.map(({ code, label, Icon: I }) => (
            <button
              key={code}
              onClick={() => act(code)}
              disabled={busy}
              className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`coaching-fb-${code}-${insight.category}`}
            ><I size={10} className="inline mr-1"/>{label}</button>
          ))}
          <button
            onClick={() => act("dismiss")}
            disabled={busy}
            className="ml-auto text-muted-foreground hover:text-signal-red text-[10px] mono"
            data-testid={`coaching-dismiss-${insight.category}`}
          ><X size={11} className="inline"/></button>
        </div>
      )}
    </div>
  );
}

export default function CoachingPanel({ compact = false, defaultOpen = false, collapsible = true }) {
  const [data, setData] = useState({ items: [], has_any: false });
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegen] = useState(false);
  const [expanded, setExpanded] = useState(defaultOpen);

  const load = () => api.get("/coaching/insights").then(r => setData(r.data || {})).catch(() => {});
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  const regen = async (e) => {
    e?.stopPropagation?.();
    setRegen(true);
    try { await api.post("/coaching/generate"); await load(); toast.success("Insights refreshed"); }
    catch (e) { toast.error("Failed to refresh"); }
    finally { setRegen(false); }
  };

  const onFeedback = async (id, code) => {
    await api.post(`/coaching/${id}/feedback`, { feedback: code });
    toast.success("Feedback saved");
    load();
  };

  const shown = compact ? data.items.slice(0, 3) : data.items;
  const count = data.items?.length || 0;

  if (!collapsible) {
    return (
      <div className="space-y-3" data-testid="coaching-panel">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-signal-yellow"/>
          <div className="label-cap">AI Coaching</div>
          <span className="ml-auto mono text-[10px] text-muted-foreground">{count} insights</span>
          <button onClick={regen} disabled={regenerating} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground" data-testid="coaching-regenerate">
            {regenerating ? "…" : "Refresh"}
          </button>
        </div>
        <CoachingBody loading={loading} shown={shown} onFeedback={onFeedback}/>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card/40" data-testid="coaching-panel">
      <div
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/20 transition-colors cursor-pointer"
        onClick={() => setExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(v => !v); }}
        aria-expanded={expanded}
        data-testid="coaching-toggle"
      >
        <Sparkles size={13} className="text-signal-yellow"/>
        <div className="text-left flex-1">
          <div className="flex items-center gap-2">
            <span className="mono text-sm">AI Coaching</span>
            <span className="chip !text-[10px] border-current text-muted-foreground">{count} insights</span>
          </div>
          {!expanded && count > 0 && (
            <div className="mono text-[10px] text-muted-foreground mt-0.5 truncate">
              {data.items[0]?.title}
            </div>
          )}
        </div>
        <button onClick={regen} disabled={regenerating} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground" data-testid="coaching-regenerate">
          {regenerating ? "…" : "Refresh"}
        </button>
        <span>{expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}</span>
      </div>
      {expanded && (
        <div className="border-t border-border/40 p-3">
          <CoachingBody loading={loading} shown={shown} onFeedback={onFeedback}/>
        </div>
      )}
    </div>
  );
}

function CoachingBody({ loading, shown, onFeedback }) {
  if (loading) return <div className="text-xs text-muted-foreground">Loading…</div>;
  if (shown.length === 0) return (
    <div className="border border-dashed border-border/40 p-4 text-xs text-muted-foreground text-center" data-testid="coaching-empty">
      <div className="mb-1 mono uppercase tracking-wide">Insufficient evidence</div>
      Coaching insights need at least 20 completed trades in a category before surfacing.
      Every trade you complete adds to the sample.
    </div>
  );
  return (
    <div className="space-y-2">
      {shown.map(i => <InsightCard key={i.id} insight={i} onFeedback={onFeedback}/>)}
    </div>
  );
}
